import {
  CognitoIdToken,
  CognitoUser,
  CognitoUserSession
} from 'amazon-cognito-identity-js';
import { parse } from 'query-string';
import * as Cookies from 'js-cookie';
import * as uuid from 'uuid';

import {
  RememberedDevice,
  isPasswordLogin,
  PasswordLoginOptions,
  MfaLoginOptions
} from './common';
import { AuthData, ForgotPasswordResponse } from '../types';
import { getConfig, getCookieOptions, clearCookieStorage } from '../utils';

const NOT_IMPLEMENTED = new Error('not implemented');

interface FakeTokens {
  accessToken: string;
  idToken: string;
  refreshToken: string;
}

/**
 *
 * @param payload
 */
function createFakeToken(payload: {}): string {
  let data = JSON.stringify(payload);

  // LOGIN-187: pad out the payload string to be a multiple
  // of 3 to remove any of the equal signs from the resulting
  // encoding
  while (data.length % 3 !== 0) {
    data += ' ';
  }

  return `BUILT.${btoa(data)}.FAKE`;
}

function createFakeTokens(payload: {}): FakeTokens {
  return {
    accessToken: createFakeToken({
      token_use: 'access',
      ...payload
    }),
    idToken: createFakeToken({
      token_use: 'id',
      ...payload
    }),
    refreshToken: createFakeToken({})
  };
}

/**
 * Log in a user.
 *
 * This method has the following call modes:
 *   1. With a username + password
 *   2. With an MFA code + MFA type
 *
 * Typically the method will be called once with a username and password. If
 * the authentication system indicates that an MFA code is needed, then this
 * method should be called again with the MFA code to complete the login.
 */
export function login(
  options: PasswordLoginOptions | MfaLoginOptions,
  useCustomAuth = true
) {
  return new Promise<AuthData>((resolve, reject) => {
    if (isPasswordLogin(options)) {
      // Generate fake tokens based on the the provided
      // email address; stub out anything else with fake
      // values
      const now = Date.now() / 1000;
      const expiry = new Date();
      const oneHour = 60 * 60 * 1000;
      expiry.setTime(expiry.getTime() + oneHour);

      // ENTS-419: using a query param, we can control what
      // experience to redirect to, effectively simulating the
      // custom claims in the JWT
      const { app } = parse(location.search);
      const defaultApp =
        app || getConfig('REACT_APP_DEFAULT_REDIRECT_APP', 'cla');

      const fakeTokens = createFakeTokens({
        // generate random UUIDs here; in BAPI, we don't care about
        // this but they shouldn't collide
        // FIXME (abe) I was hoping to use uuid5 here to
        // generate consistent UUIDs for the email addresses, but
        // something is wrong with the typings for v5
        sub: uuid.v4(),

        // the rest emulates what a typical JWT ID token from
        // cognito would look like with some obvious fake bits
        email: options.username,
        email_verified: true,
        iss: 'http://id.getbuilt.local/fakeUserPool',
        mfa_complete: 'true',
        aud: 'fakeClientId',
        exp: expiry.getTime() / 1000,
        iat: now,
        auth_time: now,
        'cognito:username': options.username,
        'custom:mfaType': 'sms',
        'apps:default': defaultApp
      });

      // Set some things in the preferred storage to allow the
      // console app logic to continue functioning normally
      // LOGIN-187: provided username is part of the cookie key
      // and must not contain any characters that trigger BAPI's
      // "Disallowed Key Characters" error.

      setAuthCookies('fakeUser', fakeTokens);

      resolve({
        username: options.username,
        email: options.username,
        ...fakeTokens
      });
    }
  });
}

export function loginEmailVerification(
  previousEmail: string,
  password: string
): Promise<AuthData> {
  return new Promise<AuthData>((resolve, reject) => {
    reject();
  });
}

/**
 * Set user preference for remembering the particular device the user is currently using
 *
 * @param user CognitoUser instance
 * @param remember if true, will inform cognito to remember the user's device; if false it
 *   will tell cognito not to remember
 */
export function setDeviceRemember(
  user: CognitoUser,
  remember: boolean | undefined
): Promise<void> {
  return new Promise((resolve, reject) => {
    reject(NOT_IMPLEMENTED);
  });
}

/**
 * Log out the currently logged in user. In fake mode,
 * this just clears out the fake tokens from local
 * storage; cookies are automatically cleared out
 * as normal in the logout action
 */
export function logout() {
  clearCookieStorage();
  return Promise.resolve();
}

/**
 * Set the initial password for a new user account.
 */
export function setInitialPassword(password: string) {
  return Promise.reject(NOT_IMPLEMENTED);
}

/**
 * Signs up a new user given the email and password. The registrationToken is used
 * for validation purposes.
 *
 * @param email
 * @param password
 * @param registrationToken
 */
export function userSignUp(
  email: string,
  password: string,
  registrationToken: string
) {
  return Promise.reject(NOT_IMPLEMENTED);
}

/**
 * Initiate the password reset process for a user
 */
export function forgotPassword(email: string) {
  return new Promise<ForgotPasswordResponse>((resolve, reject) => {
    reject(NOT_IMPLEMENTED);
  });
}

/**
 * Complete the password reset process for a user
 */
export function resetPassword(
  username: string,
  code: string,
  newPassword: string
) {
  return Promise.reject(NOT_IMPLEMENTED);
}

/**
 * Return tokens for an authenticated user
 */
export function getAuthData(user?: CognitoUser, session?: CognitoUserSession) {
  return new Promise<AuthData>((resolve, reject) => {
    reject();
  });
  // const promise: Promise<{ user?: CognitoUser; session?: CognitoUserSession }> =
  //   !user || !session ? getUser() : Promise.resolve({ user, session });
  // return promise.then(authData => {
  //   const _user = authData.user;
  //   const _session = authData.session;
  //   if (_user && _session) {
  //     const idToken = <CognitoJwtToken>_session.getIdToken();

  //     return {
  //       username: _user.getUsername(),
  //       email: idToken.payload.email,
  //       accessToken: _session.getAccessToken().getJwtToken(),
  //       idToken: idToken.getJwtToken(),
  //       refreshToken: _session.getRefreshToken().getToken()
  //     };
  //   }
  //   return;
  // })
  // .catch(() => undefined);
}

/**
 * CognitoIdToken actually extends a CognitoJwtToken class from the SDK, but
 * the TS types don't expose the payload.
 */
export interface CognitoJwtToken extends CognitoIdToken {
  payload: {
    'cognito:groups'?: string[];
    email?: string;
    [key: string]: undefined | string | string[];
  };
}

/**
 * Get the currently active user, if one is active.
 */
export function getUser(): Promise<{
  user?: CognitoUser;
  session?: CognitoUserSession;
}> {
  return new Promise<{ user?: CognitoUser; session?: CognitoUserSession }>(
    (resolve, reject) => {
      reject(NOT_IMPLEMENTED);
    }
  );
}

/**
 * Get the currently logged in user. Throw if no user is authenticated.
 */
export function getAuthorizedUser(): Promise<{
  user: CognitoUser;
  session: CognitoUserSession;
}> {
  return getUser().then(({ user, session }) => {
    if (!user || !session) {
      throw new Error('No user is currently logged in');
    }
    return { user, session };
  });
}

/**
 * Retrieve a validation code that can be used to register a TOTP
 * authenticator.
 */
export function getTokenSecret() {
  return Promise.reject(NOT_IMPLEMENTED);
}

/**
 *
 */
export function exchangeAuthCode(
  clientId: string,
  authCode: string,
  redirectUrl?: string
) {
  return Promise.reject(NOT_IMPLEMENTED);
}

/**
 * Complete the registration process for a TOTP authenticator.
 */
export function verifySoftwareToken(
  code: string,
  authenticatorName = 'authenticator'
) {
  return Promise.reject(NOT_IMPLEMENTED);
}

export function setSmsPhoneNumber(phoneNumber: string) {
  return setUserAttributes({ phone_number: phoneNumber });
}

export function resendSmsCode() {
  return Promise.reject(NOT_IMPLEMENTED);
}

export function verifySmsPhoneNumber(code: string) {
  return verifyUserAttribute('phone_number', code);
}

/**
 * Enable MFA for a user with a verified software token or SMS phone number
 */
export function enableMfa(type: 'sms' | 'totp') {
  return Promise.reject(NOT_IMPLEMENTED);
}

/**
 * Disable MFA on a user account
 */
export function disableMfa() {
  return Promise.reject(NOT_IMPLEMENTED);
}

/**
 * Return a list of remembered devices
 */
export function getRememberedDevices(): Promise<RememberedDevice[]> {
  return getAuthorizedUser().then(
    ({ user }) =>
      new Promise<RememberedDevice[]>((resolve, reject) => {
        user.listDevices(10, null, {
          onSuccess(result) {
            resolve(
              result.Devices.map((device) => ({
                created: device.DeviceCreateDate,
                id: device.DeviceKey,
                name: device.DeviceAttributes.find(
                  (attr) => attr.Name === 'device_name'
                )!.Value,
                lastAuthenticated: device.DeviceLastAuthenticatedDate,
                lastModified: device.DeviceLastModifiedDate,
                attributes: device.DeviceAttributes.reduce(
                  (allAttrs, attr) => ({
                    ...allAttrs,
                    [attr.Name]: attr.Value
                  }),
                  {} as { [attribute: string]: string }
                )
              }))
            );
          },
          onFailure(error) {
            reject(error);
          }
        });
      })
  );
}

/**
 * Forget the specified device, or all devices if no ID is specified
 */
export function forgetDevice(deviceId?: string): Promise<boolean> {
  return Promise.reject(NOT_IMPLEMENTED);
}

/**
 * Get the attributes for the authenticated user
 */
export function getUserData() {
  return Promise.reject(NOT_IMPLEMENTED);
}

/**
 * Set attributes on the authenticated user
 */
export function setUserAttributes(attrs: { [name: string]: string }) {
  return Promise.reject(NOT_IMPLEMENTED);
}

/**
 * Verify an attribute on the authenticated user
 */
export function verifyUserAttribute(name: string, code: string) {
  return Promise.reject(NOT_IMPLEMENTED);
}

/**
 * Delete an attribute on the authenticated user
 */
export function deleteUserAttribute(name: string) {
  return Promise.reject(NOT_IMPLEMENTED);
}

/**
 * Add a user to the user pool
 */
export function addUser(email: string, group: string) {
  return Promise.resolve({
    user: {
      Name: 'foo',
      Username: 'foo',
      Attributes: [{ Name: 'foo', Value: 'bar' }]
    },
    groups: []
  });
}

/**
 * Delete a user from the user pool
 */
export function deleteUser(username: string) {
  return Promise.reject(NOT_IMPLEMENTED);
}

/**
 * Get all users in the user pool
 */
export function getUsers() {
  return Promise.resolve([
    {
      user: {
        Username: 'foo',
        UserStatus: 'foo',
        Enabled: false,
        Attributes: [{ Name: 'foo', Value: 'bar' }]
      },
      groups: []
    }
  ]);
}

export function getGroups() {
  return Promise.resolve([
    {
      GroupName: 'foo',
      UserPoolId: 'foo',
      Description: 'foo'
    }
  ]);
}

/**
 * Enable a user account
 */
export function enableUser(userId: string) {
  return Promise.reject(NOT_IMPLEMENTED);
}

/**
 * Mocks the cookies that the real cognito library will use
 * to determine a user's session, refresh tokens, etc.
 *
 * @param user the username/email of the user
 * @param fakeTokens generated fake tokens
 */
function setAuthCookies(user: string, fakeTokens: FakeTokens) {
  const cookieOptions = getCookieOptions();
  const prefix = `CognitoIdentityServiceProvider.${getConfig(
    'REACT_APP_CLIENT_ID'
  )}`;
  Cookies.set(`${prefix}.LastAuthUser`, user, cookieOptions);
  Cookies.set(`${prefix}.${user}.deviceKey`, 'fakeDeviceKey', cookieOptions);
  Cookies.set(`${prefix}.${user}.idToken`, fakeTokens.idToken, cookieOptions);
  Cookies.set(
    `${prefix}.${user}.accessToken`,
    fakeTokens.accessToken,
    cookieOptions
  );
  Cookies.set(
    `${prefix}.${user}.refreshToken`,
    fakeTokens.refreshToken,
    cookieOptions
  );
}
